import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import adminService from '../../services/adminService';
import billingService from '../../services/billingService';
import { format, isToday, isTomorrow, isPast } from 'date-fns';
import PaymentDetailsModal from './AdminPaymentDetailsModal';
import SwitchPrimaryDoctorModal from './SwitchPrimaryDoctorModal';
import AdminPendingAppointments from './AdminDoctorPendingAppointments';

import '../../styles/components/admin/DoctorAssociations.css';
import '../../styles/components/admin/AdminAppointmentHistory.css';

import ViewIcon from '../../assets/icons/indicators/icon_view.svg'; 
import SwitchIcon from '../../assets/icons/indicators/switch_icon.svg'; 


const AdminDoctorAppointmentsTab = ({ patient, patientId, tenantId }) => {
  const [loading, setLoading] = useState(true);
  const [associations, setAssociations] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [billingRecords, setBillingRecords] = useState([]);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const [showPaymentDetails, setShowPaymentDetails] = useState(false);
  const [selectedBilling, setSelectedBilling] = useState(null);
  
  // New state for doctor switching modal
  const [showSwitchDoctorModal, setShowSwitchDoctorModal] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState(null);

  // Filter states
  const [appointmentSearchQuery, setAppointmentSearchQuery] = useState('');
  const [selectedDoctorFilter, setSelectedDoctorFilter] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [billingStatusFilter, setBillingStatusFilter] = useState('all');

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Modal states
  const [showAppointmentDetails, setShowAppointmentDetails] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);

  const handleViewPaymentDetails = (billing) => {
    setSelectedBilling(billing);
    setShowPaymentDetails(true);
  };

  const handleMarkAsPaid = async (billingId) => {
    try {
      // Show loading state if desired
      // setIsProcessing(true);
      
      console.log(`Marking payment ${billingId} as paid in tenant ${tenantId}`);
      
      // Call the admin service to mark the payment as paid
      const response = await adminService.markBillingAsPaid(billingId, tenantId, {
        paymentMethod: 'admin_confirmation', // Special payment method for admin confirmations
        transactionId: `ADMIN-${Date.now()}`, // Generate a transaction ID
        confirmationNotes: 'Payment confirmed by admin'
      });
      
      if (response.success) {
        // Show success message
        alert('Payment has been successfully marked as paid');
        
        // Refresh the data to show updated status
        await fetchPatientDoctorData();
        
        // Close the modal
        setShowPaymentDetails(false);
        setSelectedBilling(null);
      } else {
        alert('Failed to mark payment as paid: ' + (response.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error marking payment as paid:', error);
      alert('Failed to mark payment as paid: ' + (error.message || 'Unknown error'));
    } finally {
      // Clear loading state
      // setIsProcessing(false);
    }
  };

  // Handler for switching primary doctor
  const handleSwitchDoctor = (doctor) => {
    setSelectedDoctor(doctor);
    setShowSwitchDoctorModal(true);
  };

  const getBillingForAppointment = (appointment) => {
  return billingRecords.find(billing => 
    billing.appointmentId === appointment._id ||
    // If appointmentId doesn't exist, match by date and doctor
    (billing.appointmentDate && appointment.appointmentDate &&
     new Date(billing.appointmentDate).toDateString() === new Date(appointment.appointmentDate).toDateString() &&
     billing.doctor?._id === appointment.doctor?._id)
  );
};

  useEffect(() => {
    fetchPatientDoctorData();
  }, [patientId, tenantId]);

  const fetchPatientDoctorData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch patient-doctor associations
      const associationsResponse = await adminService.getPatientDoctorAssociations(patientId, tenantId);
      
      if (associationsResponse.success) {
        setAssociations(associationsResponse.data || []);
        
        // If there are associations, fetch appointments and billing
        if (associationsResponse.data && associationsResponse.data.length > 0) {
          // Fetch appointments
          const appointmentsResponse = await adminService.getPatientAppointments(patientId, tenantId);
          
          if (appointmentsResponse.success) {
            setAppointments(appointmentsResponse.data || []);
          }

          // Fetch billing records
          const billingResponse = await adminService.getPatientBillingRecords(patientId, tenantId);
          
          if (billingResponse.success) {
            setBillingRecords(billingResponse.data || []);
          }
        }
      } else {
        setError('Failed to load doctor associations');
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching patient-doctor data:', err);
      setError('Failed to load doctor and appointment data');
      setLoading(false);
    }
  };

  const handleViewDoctorProfile = (doctorId) => {
    navigate(`/admin/doctors/${doctorId}${tenantId ? `?tenantId=${tenantId}` : ''}`);
  };

  const handleViewAppointmentDetails = (appointment) => {
    setSelectedAppointment(appointment);
    setShowAppointmentDetails(true);
  };

  const formatAppointmentTime = (dateString) => {
    try {
      const date = new Date(dateString);
      const time = format(date, 'h:mm a');
      
      if (isToday(date)) {
        return `Today at ${time}`;
      } else if (isTomorrow(date)) {
        return `Tomorrow at ${time}`;
      } else {
        return format(date, 'MMM dd, yyyy') + ' at ' + time;
      }
    } catch (error) {
      return dateString;
    }
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'pending': return 'pending';
      case 'scheduled': return 'scheduled';
      case 'completed': return 'completed';
      case 'cancelled': return 'cancelled';
      case 'declined': return 'declined';
      default: return 'default';
    }
  };

  // Filter appointments by status
  const getPendingAppointments = () => {
    return appointments.filter(apt => 
      apt.status && apt.status.toLowerCase() === 'pending'
    );
  };

  const getScheduledAppointments = () => {
    return appointments.filter(apt => 
      apt.status === 'Scheduled' && !isPast(new Date(apt.appointmentDate))
    );
  };

  const getAppointmentHistory = () => {
    let filtered = appointments.filter(apt => 
      apt.status === 'Completed' || 
      apt.status === 'Cancelled' || 
      apt.status === 'Declined' ||
      (apt.status === 'Scheduled' && isPast(new Date(apt.appointmentDate)))
    );

    // Apply filters
    if (appointmentSearchQuery.trim() !== '') {
      const query = appointmentSearchQuery.toLowerCase();
      filtered = filtered.filter(apt => 
        apt.doctor?.firstName?.toLowerCase().includes(query) || 
        apt.doctor?.lastName?.toLowerCase().includes(query) || 
        apt.appointmentType?.toLowerCase().includes(query)
      );
    }

    if (selectedDoctorFilter) {
      filtered = filtered.filter(apt => apt.doctor?._id === selectedDoctorFilter);
    }

    if (selectedStatus) {
      filtered = filtered.filter(apt => apt.status === selectedStatus);
    }

    return filtered.sort((a, b) => new Date(b.appointmentDate) - new Date(a.appointmentDate));
  };

  const getFilteredBillingRecords = () => {
    let filtered = billingRecords;

    if (billingStatusFilter !== 'all') {
      filtered = filtered.filter(record => record.status === billingStatusFilter);
    }

    if (selectedDoctorFilter) {
      filtered = filtered.filter(record => record.doctor?._id === selectedDoctorFilter);
    }

    return filtered;
  };



  const paginateData = (data) => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return {
      paginatedData: data.slice(startIndex, endIndex),
      totalPages: Math.ceil(data.length / itemsPerPage)
    };
  };

  // Get active associations (current doctors)
  const getActiveAssociations = () => {
    return associations.filter(assoc => assoc.status.toLowerCase() === 'active');
  };

  if (loading) {
    return (
      <div className="admin-tab-panel">
        <div className="admin-loading-container">
          <div className="admin-loading-spinner"></div>
          <p>Loading doctor associations and appointments...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-tab-panel">
     {/* Doctor Associations Section */}
<div className="admin-section-header">
  <h3 className="admin-section-title">Mental Health Professional</h3>
</div>

{error && (
  <div className="admin-error-alert" style={{ marginBottom: '20px' }}>
    <span className="admin-error-icon">⚠️</span>
    <span className="admin-error-message">{error}</span>
  </div>
)}

{associations.length === 0 ? (
  <div className="admin-empty-state">
    <h3>No Doctor Associations</h3>
    <p>{patient.firstName} is not currently associated with any doctors.</p>
    <button 
      className="admin-primary-button"
      onClick={() => navigate(`/admin/users/${patientId}/edit${tenantId ? `?tenantId=${tenantId}` : ''}`)}
    >
      Edit Patient
    </button>
  </div>
) : (
  <div className="mental-health-professionals-container">
    {associations.map((assoc) => (
      <div key={assoc._id} className="mental-health-professional-card">
        {/* Professional Header */}
        <div className="professional-header">
          <div className="professional-avatar">
            <img 
              src={assoc.doctor.profilePicture || '/default-doctor-avatar.png'} 
              alt={`Dr. ${assoc.doctor.firstName} ${assoc.doctor.lastName}`}
              onError={(e) => {
                e.target.src = '/default-doctor-avatar.png';
              }}
            />
          </div>
          <div className="professional-title-section">
            <h4 className="professional-name">
              Dr. {assoc.doctor.firstName} {assoc.doctor.lastName}
            </h4>
            <p className="professional-specialty">
              {assoc.doctor.specialty || assoc.doctor.specialization || 'Psychotherapist'}
            </p>
          </div>
          
          {/* Updated Actions Section - Now with buttons instead of dropdown */}
          <div className="professional-actions-menu">
            <span className="actions-label">Actions</span>
            <div className="action-buttons-group">
             <button
                  className="professional-action-btn view-profile-btn"
                  onClick={() => handleViewDoctorProfile(assoc.doctor._id)}
                >
                  <img src={ViewIcon} alt="View Profile" width="14" height="14" />
                </button>

                {assoc.status.toLowerCase() === 'active' && (
                  <button
                    className="professional-action-btn switch-doctor-btn"
                    onClick={() => handleSwitchDoctor(assoc.doctor)}
                  >
                    <img src={SwitchIcon} alt="Switch Doctor" width="14" height="14" />
                  </button>
                )}
          
            </div>
          </div>
        </div>

        {/* Contact Information Grid - Updated to show clinic instead of location */}
        <div className="professional-contact-grid">
          <div className="contact-info-item">
            <div className="contact-icon">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M2 3C2 2.44772 2.44772 2 3 2H4.15287C4.64171 2 5.0589 2.35341 5.13927 2.8356L5.8356 6.1644C5.92336 6.69471 5.66435 7.22874 5.18198 7.45198L4 8C4 10.2091 5.79086 12 8 12L8.54802 10.818C8.77126 10.3356 9.30529 10.0766 9.8356 10.1644L13.1644 10.8607C13.6466 10.9411 14 11.3583 14 11.8471V13C14 13.5523 13.5523 14 13 14H12C6.47715 14 2 9.52285 2 4V3Z" stroke="currentColor" strokeWidth="1.5"/>
              </svg>
            </div>
            <span className="contact-text">
              {assoc.doctor.personalContactNumber || 
               assoc.doctor.clinicContactNumber || 
               assoc.doctor.phoneNumber || 
               'Not provided'}
            </span>
          </div>
          
          <div className="contact-info-item">
            <div className="contact-icon">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M2.5 4.5L8 8.5L13.5 4.5M2.5 4.5C2.22386 4.5 2 4.72386 2 5V11C2 11.5523 2.44772 12 3 12H13C13.5523 12 14 11.5523 14 11V5C14 4.72386 13.7761 4.5 13.5 4.5M2.5 4.5H13.5" stroke="currentColor" strokeWidth="1.5"/>
              </svg>
            </div>
            <span className="contact-text">
              {assoc.doctor.email}
            </span>
          </div>
          
          <div className="contact-info-item">
            <div className="contact-icon">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M2 2H14C14.5523 2 15 2.44772 15 3V13C15 13.5523 14.5523 14 14 14H2C1.44772 14 1 13.5523 1 13V3C1 2.44772 1.44772 2 2 2Z" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M8 6V10M6 8H10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </div>
            <span className="contact-text">
              {assoc.tenantName || assoc.doctor.clinicName || 'Clinic not specified'}
            </span>
          </div>
          
          <div className="contact-info-item">
            <div className="contact-icon">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M2 4L8 8L14 4M2 4V12C2 12.5523 2.44772 13 3 13H13C13.5523 13 14 12.5523 14 12V4M2 4C2 3.44772 2.44772 3 3 3H13C13.5523 3 14 3.44772 14 4" stroke="currentColor" strokeWidth="1.5"/>
              </svg>
            </div>
            <span className="contact-text">
              ₱{assoc.doctor.consultationFee || 'Contact for rates'}
            </span>
          </div>
        </div>
      </div>
    ))}
  </div>
)}

     <AdminPendingAppointments
        pendingAppointments={getPendingAppointments()}
        patient={patient}
        tenantId={tenantId}
        onAppointmentUpdated={fetchPatientDoctorData}
        onViewAppointmentDetails={handleViewAppointmentDetails}
      />

      {/* Scheduled Appointments */}
      {getScheduledAppointments().length > 0 && (
        <div className="admin-section-card">
          <h3 className="admin-section-title">
            Upcoming Appointments ({getScheduledAppointments().length})
          </h3>
          <div className="admin-appointments-table">
            <div className="admin-table-header">
              <div className="admin-header-cell">Date & Time</div>
              <div className="admin-header-cell">Doctor</div>
              <div className="admin-header-cell">Type</div>
              <div className="admin-header-cell">Duration</div>
              <div className="admin-header-cell">Status</div>
              <div className="admin-header-cell">Actions</div>
            </div>

            {getScheduledAppointments().map((appt) => (
              <div key={appt._id} className="admin-table-row">
                <div className="admin-table-cell">
                  {formatAppointmentTime(appt.appointmentDate)}
                </div>
                <div className="admin-table-cell">
                  Dr. {appt.doctor?.firstName} {appt.doctor?.lastName}
                </div>
                <div className="admin-table-cell">
                  {appt.appointmentType || 'General Consultation'}
                </div>
                <div className="admin-table-cell">
                  {appt.duration || 30} min
                </div>
                <div className="admin-table-cell">
                  <span className="admin-status-indicator admin-status-scheduled">
                    Scheduled
                  </span>
                </div>
                <div className="admin-table-cell">
                  <button
                    className="admin-action-btn admin-view-button"
                    onClick={() => handleViewAppointmentDetails(appt)}
                  >
                    View Details
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Appointment History */}
        <div className="admin-section-header">
          <h3 className="admin-section-title">Appointment History</h3>
          <div className="admin-filter-controls">
            <input
              type="text"
              placeholder="Search appointments..."
              value={appointmentSearchQuery}
              onChange={(e) => setAppointmentSearchQuery(e.target.value)}
              className="admin-search-input"
            />
            <select
              value={selectedDoctorFilter}
              onChange={(e) => setSelectedDoctorFilter(e.target.value)}
              className="admin-filter-select"
            >
              <option value="">All Doctors</option>
              {associations.map(assoc => (
                <option key={assoc.doctor._id} value={assoc.doctor._id}>
                  Dr. {assoc.doctor.firstName} {assoc.doctor.lastName}
                </option>
              ))}
            </select>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="admin-filter-select"
            >
              <option value="">All Status</option>
              <option value="Completed">Completed</option>
              <option value="Cancelled">Cancelled</option>
              <option value="Declined">Declined</option>
            </select>
          </div>
        </div>

        {getAppointmentHistory().length === 0 ? (
          <div className="admin-empty-state">
            <p>No appointment history found.</p>
          </div>
        ) : (
          <>
            <div className="admin-appointment-history-table">
  <div className="admin-appointment-history-header">
    <div className="admin-appointment-history-header-cell">Date & Time</div>
    <div className="admin-appointment-history-header-cell">Doctor</div>
    <div className="admin-appointment-history-header-cell">Type</div>
    <div className="admin-appointment-history-header-cell">Duration</div>
    <div className="admin-appointment-history-header-cell">Status</div>
    <div className="admin-appointment-history-header-cell">Actions</div>
  </div>

  {paginateData(getAppointmentHistory()).paginatedData.map((appt) => (
    <div key={appt._id} className="admin-appointment-history-row">
      <div className="admin-appointment-history-cell">
        {formatAppointmentTime(appt.appointmentDate)}
      </div>
      <div className="admin-appointment-history-cell">
        Dr. {appt.doctor?.firstName} {appt.doctor?.lastName}
      </div>
      <div className="admin-appointment-history-cell">
        {appt.appointmentType || 'General Consultation'}
      </div>
      <div className="admin-appointment-history-cell">
        {appt.duration || 30} min
      </div>
      <div className="admin-appointment-history-cell">
        <span className={`admin-status-indicator admin-status-${getStatusColor(appt.status)}`}>
          {appt.status}
        </span>
      </div>
      <div className="admin-appointment-history-cell">
        <button
          className="admin-action-btn admin-view-button"
          onClick={() => handleViewAppointmentDetails(appt)}
        >
          View Details
        </button>
      </div>
    </div>
  ))}
</div>

            {paginateData(getAppointmentHistory()).totalPages > 1 && (
              <div className="admin-pagination">
                <button
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                  className="admin-pagination-btn"
                >
                  Previous
                </button>
                <span className="admin-pagination-info">
                  Page {currentPage} of {paginateData(getAppointmentHistory()).totalPages}
                </span>
                <button
                  disabled={currentPage === paginateData(getAppointmentHistory()).totalPages}
                  onClick={() => setCurrentPage(currentPage + 1)}
                  className="admin-pagination-btn"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}

      {/* Billing and Payment Information */}
        <div className="admin-section-header">
          <h3 className="admin-section-title">Billing & Payment Information</h3>
          <div className="admin-billing-filters">
            <select
              value={billingStatusFilter}
              onChange={(e) => setBillingStatusFilter(e.target.value)}
              className="admin-filter-select"
            >
              <option value="all">All Status</option>
              <option value="paid">Paid</option>
              <option value="pending">Pending</option>
              <option value="overdue">Overdue</option>
            </select>
          </div>
        </div>

        {getFilteredBillingRecords().length === 0 ? (
          <div className="admin-empty-state">
            <p>No billing records found.</p>
          </div>
        ) : (
          <div className="admin-billing-payment-table">
              <div className="admin-billing-payment-header">
                <div className="admin-billing-payment-header-cell">Appointment Date</div>
                <div className="admin-billing-payment-header-cell">Doctor</div>
                <div className="admin-billing-payment-header-cell">Session Fee</div>
                <div className="admin-billing-payment-header-cell">Status</div>
                <div className="admin-billing-payment-header-cell">Payment Method</div>
                <div className="admin-billing-payment-header-cell">Actions</div>
              </div>

              {getFilteredBillingRecords().map((billing) => (
                <div key={billing._id} className="admin-billing-payment-row">
                  <div className="admin-billing-payment-cell">
                    {format(new Date(billing.appointmentDate), 'MMM dd, yyyy')}
                  </div>
                  <div className="admin-billing-payment-cell">
                    Dr. {billing.doctor?.firstName} {billing.doctor?.lastName}
                  </div>
                  <div className="admin-billing-payment-cell">
                    ₱{billing.amount || '0.00'}
                  </div>
                  <div className="admin-billing-payment-cell">
                    <span className={`admin-billing-status admin-billing-${billing.status}`}>
                      {billing.status.charAt(0).toUpperCase() + billing.status.slice(1)}
                    </span>
                  </div>
                  <div className="admin-billing-payment-cell">
                    {billing.paymentMethod || 'Not specified'}
                  </div>
                  <div className="admin-billing-payment-cell">
                    <button
                      className="admin-action-btn admin-view-button"
                      onClick={() => handleViewPaymentDetails(billing)}
                    >
                      View Details
                    </button>
                  </div>
                </div>
              ))}
            </div>
        )}

      {/* Appointment Details Modal */}
{showAppointmentDetails && selectedAppointment && (
  <div className="admin-appointment-details-modal-overlay">
    <div className="admin-appointment-details-modal-container">
      <div className="admin-appointment-details-modal-header">
        <button 
          className="admin-appointment-details-modal-close"
          onClick={() => {
            setShowAppointmentDetails(false);
            setSelectedAppointment(null);
          }}
        >
          ×
        </button>
      </div>
      
      <div className="admin-appointment-details-modal-body">
        {/* Patient Section */}
        <h3 className="admin-appointment-details-modal-title">Appointment Details</h3>
        <h4 className="admin-appointment-section-title">Patient Information</h4>
        <div className="admin-appointment-patient-section">
          <div className="admin-appointment-patient-info">
            <div className="admin-appointment-patient-avatar">
              {patient.profilePicture && patient.profilePicture !== 'default-profile.png' ? (
                <img 
                  src={patient.profilePicture} 
                  alt="Patient Profile" 
                  className="admin-appointment-patient-image"
                />
              ) : (
                <div className="admin-appointment-patient-initials">
                  {patient.firstName?.[0] || 'P'}{patient.lastName?.[0] || ''}
                </div>
              )}
            </div>
            <div className="admin-appointment-patient-details">
              <div className="admin-appointment-detail-row">
                <span className="admin-appointment-detail-value admin-appointment-patient-name">
                  {patient.firstName} {patient.lastName}
                </span>
              </div>
              <div className="admin-appointment-detail-row">
                <span className="admin-appointment-detail-value admin-appointment-patient-email">
                  {patient.email}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Schedule Details Section */}
        <h4 className="admin-appointment-section-title">Schedule Details</h4>
        <div className="admin-appointment-schedule-section">
          
          <div className="admin-appointment-schedule-grid">
            <div className="admin-appointment-detail-row">
              <span className="admin-appointment-detail-label">Doctor:</span>
              <span className="admin-appointment-detail-value">
                Dr. {selectedAppointment.doctor?.firstName} {selectedAppointment.doctor?.lastName}
              </span>
            </div>
            <div className="admin-appointment-detail-row">
              <span className="admin-appointment-detail-label">Date:</span>
              <span className="admin-appointment-detail-value">
                {format(new Date(selectedAppointment.appointmentDate), 'MMMM dd, yyyy')}
              </span>
            </div>
            <div className="admin-appointment-detail-row">
              <span className="admin-appointment-detail-label">Time:</span>
              <span className="admin-appointment-detail-value">
                {format(new Date(selectedAppointment.appointmentDate), 'hh:mm a')}
              </span>
            </div>
          </div>
        </div>

        {/* Billing Information Section */}
<h4 className="admin-appointment-section-title">Billing Information</h4>
<div className="admin-appointment-billing-section">

  <div className="admin-appointment-billing-grid">
    {(() => {
      const billingInfo = getBillingForAppointment(selectedAppointment);
      return (
        <>
          <div className="admin-appointment-detail-row">
            <span className="admin-appointment-detail-label">Session Fee:</span>
            <span className="admin-appointment-detail-value">
              ₱{billingInfo?.amount || 
                 selectedAppointment.sessionFee || 
                 selectedAppointment.doctor?.consultationFee || 
                 '0.00'}
            </span>
          </div>
          <div className="admin-appointment-detail-row">
            <span className="admin-appointment-detail-label">Payment Status:</span>
            <span className={`admin-appointment-payment-status admin-appointment-payment-${billingInfo?.status || 'pending'}`}>
              {billingInfo?.status ? 
                billingInfo.status.charAt(0).toUpperCase() + billingInfo.status.slice(1) 
                : 'Pending'}
            </span>
          </div>
          {billingInfo?.paymentMethod && (
            <div className="admin-appointment-detail-row">
              <span className="admin-appointment-detail-label">Payment Method:</span>
              <span className="admin-appointment-detail-value">
                {billingInfo.paymentMethod}
              </span>
            </div>
          )}
        </>
      );
      })()}
          </div>
        </div>
      </div>
    </div>
  </div>
)}

      {/* Payment Details Modal */}
      <PaymentDetailsModal
        isOpen={showPaymentDetails}
        onClose={() => {
          setShowPaymentDetails(false);
          setSelectedBilling(null);
        }}
        billing={selectedBilling}
        patient={patient}
        onMarkAsPaid={handleMarkAsPaid}
      />

      {/* Switch Primary Doctor Modal */}
      <SwitchPrimaryDoctorModal
        isOpen={showSwitchDoctorModal}
        onClose={() => {
          setShowSwitchDoctorModal(false);
          setSelectedDoctor(null);
        }}
        patient={patient}
        currentDoctor={selectedDoctor}
        tenantId={tenantId}
        onDoctorChanged={fetchPatientDoctorData}
      />
    </div>
  );
};

export default AdminDoctorAppointmentsTab;